import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { useDebounceValue } from 'usehooks-ts';

import { ManagementChildDtoIn, ChildrenQueryParams } from '@/models/in/ManagementChildDtoIn';
import { RelationshipStatus } from '@/models/common/RelationshipStatus';
import DataTable from '@/components/ui/DataTable/DataTable';
import SelectAllHeader from '@/components/ui/DataTable/SelectAllHeader';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { usePostAuthApiInfiniteQuery } from '@/hooks/UseApiCalls';
import { getStatusColorSets } from '@/helpers/cssHelpers';
import AdditionalInfo from './AdditionalInfo/AdditionalInfo';
import BulkStatusChangeDialog from './BulkStatusChangeDialog/BulkStatusChangeDialog';
import EditChildDialog from '@/components/children/EditChildDialog/EditChildDialog';
import DeleteChildDialog from './DeleteChildDialog/DeleteChildDialog';
import OfficeCommentsDialog from './OfficeCommentsDialog/OfficeCommentsDialog';
import FiltersArea from './FiltersArea';
import CustomMenu from '@/components/ui/Menu';
import useTableDensity from '@/hooks/useTableDensity';
import editIcon from '@/assets/icons/edit.svg';
import closeIcon from '@/assets/icons/close.svg';
import styles from './ChildrenManagementPage.module.css';

// Constants
const SEARCH_DEBOUNCE_MS = 300;
const DENSITY_STORAGE_KEY = 'table.density.childrenManagement';

// Map column IDs to backend sort field names
const SORT_FIELD_MAP: Record<string, string> = {
  childName: 'ChildName',
  israeliId: 'IsraeliId',
  birthDate: 'BirthDate',
  parentName: 'ParentName',
  status: 'Status',
  requestDate: 'CreatedAt',
};

const ChildrenManagementPage = () => {
  const { t } = useTranslation();
  const [selectedRow, setSelectedRow] = useState<ManagementChildDtoIn>();
  const [selectedTab, setSelectedTab] = useState<'child' | 'parent'>('child');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounceValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');

  // Page size state
  const [pageSize, setPageSize] = useState<number>(50);

  // Memoize styles from CSS variables (read once)
  const statusColorSets = useMemo(() => getStatusColorSets(), []);

  // Bulk status change state
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [selectedNewStatus, setSelectedNewStatus] = useState<RelationshipStatus | null>(null);
  const [selectedForBulk, setSelectedForBulk] = useState<ManagementChildDtoIn[]>([]);

  // Edit child dialog state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [childToEdit, setChildToEdit] = useState<ManagementChildDtoIn | null>(null);

  // Delete child dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [childToDelete, setChildToDelete] = useState<ManagementChildDtoIn | null>(null);

  // Office comments dialog state
  const [isOfficeCommentsDialogOpen, setIsOfficeCommentsDialogOpen] = useState(false);

  // Sorting state
  const [sorting, setSorting] = useState<SortingState>([{ id: 'requestDate', desc: true }]);

  // Filter state - default to "Pending Approval"
  const [statusFilters, setStatusFilters] = useState<string[]>([RelationshipStatus.PendingApproval]);
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null);

  // Build query params (uses debounced search for API calls)
  const queryParams: ChildrenQueryParams = useMemo(
    () => ({
      sortBy: SORT_FIELD_MAP[sorting[0]?.id] || 'CreatedAt',
      sortDirection: sorting[0]?.desc ? 'Desc' : 'Asc',
      statusFilters: statusFilters,
      searchTerm: debouncedSearch || undefined,
      fundId: selectedFundId ?? undefined,
    }),
    [sorting, statusFilters, debouncedSearch, selectedFundId]
  );

  const {
    data: response,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePostAuthApiInfiniteQuery<ManagementChildDtoIn, ChildrenQueryParams>('management/children', queryParams, pageSize);

  const accumulatedData = useMemo(() => response?.pages.flatMap((page) => page.data) ?? [], [response]);
  const statusCounts = response?.pages[0]?.statusCounts;

  // Refetch data when page size changes
  useEffect(() => {
    refetch();
  }, [pageSize, refetch]);

  // Sync selectedRow with data changes (e.g., after refetch).
  // If the row is no longer in the data (e.g. status changed and filtered out), close the blade.
  useEffect(() => {
    if (selectedRow && accumulatedData.length > 0) {
      const updatedRow = accumulatedData.find(
        (r) => r.parentId === selectedRow.parentId && r.childId === selectedRow.childId
      );
      if (updatedRow) {
        setSelectedRow(updatedRow);
      } else {
        setSelectedRow(undefined);
      }
    }
  }, [accumulatedData]);

  const handleRowClick = useCallback((row: ManagementChildDtoIn) => {
    setSelectedRow(row);
    setSelectedTab('child');
  }, []);

  const handleParentNameClick = useCallback((e: React.MouseEvent, row: ManagementChildDtoIn) => {
    e.stopPropagation();
    setSelectedRow(row);
    setSelectedTab('parent');
  }, []);

  const handleCloseBlade = useCallback(() => {
    setSelectedRow(undefined);
  }, []);

  // Bulk action handlers
  const handleBulkAction = useCallback((selectedRows: ManagementChildDtoIn[], newStatus: RelationshipStatus) => {
    setSelectedForBulk(selectedRows);
    setSelectedNewStatus(newStatus);
    setIsBulkDialogOpen(true);
  }, []);

  const handleBulkDialogClose = useCallback(() => {
    setIsBulkDialogOpen(false);
    setSelectedNewStatus(null);
    setSelectedForBulk([]);
  }, []);

  const handleBulkSuccess = useCallback(() => {
    handleBulkDialogClose();
    refetch();
  }, [handleBulkDialogClose, refetch]);

  // Edit child dialog handlers
  const handleOpenEditDialog = useCallback((child: ManagementChildDtoIn) => {
    setChildToEdit(child);
    setIsEditDialogOpen(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setIsEditDialogOpen(false);
    setChildToEdit(null);
  }, []);

  const handleEditSuccess = useCallback(() => {
    handleCloseEditDialog();
    refetch();
  }, [handleCloseEditDialog, refetch]);

  // Delete child dialog handlers
  const handleOpenDeleteDialog = useCallback((child: ManagementChildDtoIn) => {
    setChildToDelete(child);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setChildToDelete(null);
  }, []);

  const handleDeleteSuccess = useCallback(() => {
    handleCloseDeleteDialog();
    refetch();
  }, [handleCloseDeleteDialog, refetch]);

  // Office comments dialog handlers
  const handleOpenOfficeCommentsDialog = useCallback(() => {
    setIsOfficeCommentsDialogOpen(true);
  }, []);

  const handleCloseOfficeCommentsDialog = useCallback(() => {
    setIsOfficeCommentsDialogOpen(false);
  }, []);

  const handleOfficeCommentsSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Sorting change handler
  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
  }, []);

  const columns: ColumnDef<ManagementChildDtoIn>[] = [
    // Selection column
    {
      id: 'select',
      header: ({ table }) => <SelectAllHeader table={table} />,
      cell: Cell(cellType.Selection),
      meta: {
        className: 'max-w-[48px] min-w-[48px] w-[48px]',
        lockLayout: true,
      },
    },
    {
      id: 'childName',
      header: Col(t('childrenManagement.columns.childName')),
      accessorFn: (row) => `${row.child.firstName} ${row.child.lastName}`,
      cell: ({ getValue }) => <span>{getValue() as string}</span>,
      enableSorting: true,
      meta: {
        className: 'max-w-[180px] min-w-[180px] w-[180px]',
      },
    },
    {
      id: 'israeliId',
      header: Col(t('childrenManagement.columns.israeliId')),
      accessorFn: (row) => row.child.israeliId,
      cell: Cell(cellType.String),
      enableSorting: true,
      meta: {
        className: 'max-w-[120px] min-w-[120px] w-[120px]',
      },
    },
    {
      id: 'birthDate',
      header: Col(t('childrenManagement.columns.birthDate')),
      accessorFn: (row) => row.child.birthDate,
      cell: Cell(cellType.Date),
      enableSorting: true,
      meta: {
        className: 'max-w-[120px] min-w-[120px] w-[120px]',
      },
    },
    {
      id: 'livesWithSupported',
      header: Col(t('livesWithSupported')),
      accessorKey: 'livesWithSupported',
      cell: ({ getValue }) => {
        const livesWithSupported = getValue() as boolean;
        return <span>{t(livesWithSupported ? 'yes' : 'no')}</span>;
      },
      enableSorting: false,
      meta: {
        className: 'max-w-[100px] min-w-[100px] w-[100px]',
      },
    },
    {
      id: 'parentName',
      header: Col(t('childrenManagement.columns.parentName')),
      accessorFn: (row) => row,
      cell: ({ getValue }) => {
        const row = getValue() as ManagementChildDtoIn;
        const parentName = `${row.parent.firstName} ${row.parent.lastName}`;
        return (
          <span className={styles.clickableParentName} onClick={(e) => handleParentNameClick(e, row)}>
            {parentName}
          </span>
        );
      },
      enableSorting: true,
      meta: {
        className: 'max-w-[180px] min-w-[180px] w-[180px]',
      },
    },
    {
      id: 'parentFunds',
      header: Col(t('childrenManagement.columns.parentFunds')),
      enableSorting: false,
      accessorFn: (row) => ({
        items: row.parent.fundIds ?? [],
        tooltipId: `parent-funds-${row.childId}`,
      }),
      cell: Cell(cellType.TruncatedList),
      meta: {
        className: 'max-w-[120px] min-w-[120px] w-[120px]',
      },
    },
    {
      id: 'status',
      header: Col(t('childrenManagement.columns.status')),
      accessorKey: 'status',
      cell: ({ getValue }) => {
        const status = getValue() as RelationshipStatus;
        return (
          <span className={styles.statusBadge} style={{ backgroundColor: statusColorSets[status].backgroundAlpha, color: statusColorSets[status].border }}>
            {t(`childrenManagement.status.${status}`)}
          </span>
        );
      },
      enableSorting: true,
      meta: {
        className: 'max-w-[130px] min-w-[130px] w-[130px]',
      },
    },
    {
      id: 'requestDate',
      header: Col(t('childrenManagement.columns.requestDate')),
      accessorKey: 'requestDate',
      cell: Cell(cellType.Date),
      enableSorting: true,
      meta: {
        className: 'max-w-[120px] min-w-[120px] w-[120px]',
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <CustomMenu
          fillCell
          items={[
            {
              icon: editIcon,
              text: t('childrenManagement.editChild'),
              onClick: () => handleOpenEditDialog(row.original),
            },
            {
              icon: closeIcon,
              text: t('childrenManagement.deleteChild'),
              onClick: () => handleOpenDeleteDialog(row.original),
            },
          ]}
        />
      ),
      meta: {
        className: 'max-w-[48px] min-w-[48px] w-[48px]',
        lockLayout: true,
      },
    },
  ];

  return (
    <div>
      {selectedRow && (
        <AdditionalInfo
          data={selectedRow}
          initialTab={selectedTab}
          handleXclick={handleCloseBlade}
          onRefresh={refetch}
          onDataUpdate={setSelectedRow}
          onOpenOfficeComments={handleOpenOfficeCommentsDialog}
        />
      )}
      <DataTable
        title={t('childrenManagement.title')}
        data={accumulatedData}
        getRowId={(row) => `${row.childId}:${row.parentId}`}
        isLoading={isLoading}
        columns={columns}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="childrenManagement"
        searchPlaceholder="searchChild"
        searchTooltip="searchChildTooltip"
        sortBy={sorting[0]}
        filters={
          <FiltersArea 
            selectedFilters={statusFilters} 
            onFiltersChange={setStatusFilters} 
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            selectedFundId={selectedFundId}
            onFundChange={setSelectedFundId}
            statusCounts={statusCounts}
          />
        }
        customGlobalSearch={() => true}
        handleRowClick={handleRowClick}
        activeRecord={selectedRow}
        onLoadMore={fetchNextPage}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        loadingMoreText={t('childrenManagement.loadingMoreRecords')}
        manualSorting={true}
        onSortingChange={handleSortingChange}
        selectionActions={[
          {
            label: t('childrenManagement.bulkApprove'),
            onClick: (selectedRows) => handleBulkAction(selectedRows, RelationshipStatus.Approved),
            style: {
              backgroundColor: statusColorSets[RelationshipStatus.Approved].background,
              borderColor: statusColorSets[RelationshipStatus.Approved].border,
              color: statusColorSets[RelationshipStatus.Approved].text,
            },
          },
          {
            label: t('childrenManagement.bulkReject'),
            onClick: (selectedRows) => handleBulkAction(selectedRows, RelationshipStatus.Rejected),
            style: {
              backgroundColor: statusColorSets[RelationshipStatus.Rejected].background,
              borderColor: statusColorSets[RelationshipStatus.Rejected].border,
              color: statusColorSets[RelationshipStatus.Rejected].text,
            },
          },
          {
            label: t('childrenManagement.bulkReturnToPending'),
            onClick: (selectedRows) => handleBulkAction(selectedRows, RelationshipStatus.PendingApproval),
            style: {
              backgroundColor: statusColorSets[RelationshipStatus.PendingApproval].background,
              borderColor: statusColorSets[RelationshipStatus.PendingApproval].border,
              color: statusColorSets[RelationshipStatus.PendingApproval].text,
            },
          },
        ]}
      />

      {/* Bulk Status Change Dialog */}
      {isBulkDialogOpen && selectedNewStatus && (
        <BulkStatusChangeDialog
          open={isBulkDialogOpen}
          onClose={handleBulkDialogClose}
          selectedChildren={selectedForBulk}
          newStatus={selectedNewStatus}
          onSuccess={handleBulkSuccess}
        />
      )}
      {/* Edit Child Dialog */}
      {isEditDialogOpen && childToEdit && (
        <EditChildDialog
          open={isEditDialogOpen}
          onClose={handleCloseEditDialog}
          child={{
            childId: childToEdit.childId,
            firstName: childToEdit.child.firstName,
            lastName: childToEdit.child.lastName,
            israeliId: childToEdit.child.israeliId,
            birthDate: childToEdit.child.birthDate,
          }}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Delete Child Dialog */}
      {isDeleteDialogOpen && childToDelete && (
        <DeleteChildDialog
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          child={childToDelete}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {/* Office Comments Dialog */}
      {selectedRow && (
        <OfficeCommentsDialog
          open={isOfficeCommentsDialogOpen}
          onClose={handleCloseOfficeCommentsDialog}
          parentId={selectedRow.parentId}
          childId={selectedRow.childId}
          officeComments={selectedRow.officeComments || []}
          onSuccess={handleOfficeCommentsSuccess}
        />
      )}
    </div>
  );
};

export default ChildrenManagementPage;
